import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';

import ConfirmButton from '~/components/Common/Elements/ConfirmButton';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import ErrorDisplay from '~/components/Common/ErrorDisplay';

import {
  BidClassifications,
  RuleDefinitionData,
  deleteAllCustomRules,
  getRules,
  listGoogleAdsCampaignsConfig,
} from '~/services/PublicOfferFeedService';
import * as publicOfferFeedService from '~/services/PublicOfferFeedService';

interface BidAutomationRulesProps {
  customerId: string;
  campaignId: string;
  adGroupId?: string;
  fetchData?: (customerId: string, campaignId: string, adGroupId?: string) => void;
  isAdGroupMultiSelect?: boolean;
  selectedAdGroupIds?: Array<GridRowId>;
}

const RULE_HEADINGS = {
  spend: 'Spend (AUD)',
  margin: 'Margin (AUD)',
  mpv: 'Margin Per View (AUD)',
  views: 'Views',
  clicks: 'Clicks',
  impressions: 'Impressions',
  ctr: 'CTR',
  cpc: 'CPC',
  impression_share: 'Impression Share',
  mroas: 'Margin Roas',
  conversion_value: 'Conversion Value',
  conversions: 'Conversions',
  margin_per_view_change_3d_vs_7d_pct: 'Percentage of Margin per view change from 3 days to 7 days',
  margin_per_view_change_1d_vs_7d_pct: 'Percentage of Margin per view change from 1 days to 7 days',
  conversion_rate_change_3d_vs_7d_pct: 'Percentage of Conversion rate change from 3 days to 7 days',
  conversion_rate_change_1d_vs_7d_pct: 'Percentage of Conversion rate change from 1 days to 7 days',
};

const ALL_METRICS_SECTION = [
  'spend',
  'margin',
  'mpv',
  'views',
  'clicks',
  'impressions',
  'ctr',
  'cpc',
  'impression_share',
  'mroas',
  'conversion_value',
  'conversions',
] as const;

const SEARCH_METRICS_SECTION = [
  'margin_per_view_change_3d_vs_7d_pct',
  'margin_per_view_change_1d_vs_7d_pct',
  'conversion_rate_change_3d_vs_7d_pct',
  'conversion_rate_change_1d_vs_7d_pct',
] as const;

const RULE_LEVELS = ['H', 'M', 'L'] as const;
const LEVEL_LABELS = { H: 'High', M: 'Medium', L: 'Low' };

type RuleDefinitionFormData = {
  [K in (typeof ALL_METRICS_SECTION)[number] | (typeof SEARCH_METRICS_SECTION)[number]]: {
    H: number;
    M: number;
    L: number;
  };
};

const generateDefaultValues = (): RuleDefinitionFormData => {
  return Object.fromEntries(
    [...ALL_METRICS_SECTION, ...SEARCH_METRICS_SECTION].map((metric) => [metric, { H: 0, M: 0, L: 0 }]),
  ) as RuleDefinitionFormData;
};

const generateResetValues = (ruleDefinitions: RuleDefinitionData): RuleDefinitionFormData => {
  return Object.fromEntries(
    [...ALL_METRICS_SECTION, ...SEARCH_METRICS_SECTION].map((metric) => [
      metric,
      {
        H: ruleDefinitions[metric]?.H ?? 0,
        M: ruleDefinitions[metric]?.M ?? 0,
        L: ruleDefinitions[metric]?.L ?? 0,
      },
    ]),
  ) as RuleDefinitionFormData;
};

export function BidAutomationRules({
  customerId,
  campaignId,
  adGroupId,
  fetchData,
  isAdGroupMultiSelect,
  selectedAdGroupIds,
}: BidAutomationRulesProps) {
  const [ruleFetchingState, setRuleFetchingState] = useState<Utils.FetchingState>('idle');
  const [hasRules, setHasRules] = useState(false);
  const [ruleClassifications, setRuleClassifications] = useState<BidClassifications>(null);
  const [ruleDefinitions, setRuleDefinitions] = useState<RuleDefinitionData>(null);
  const [primaryMetrics, setPrimaryMetrics] = useState<Array<string>>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<Array<string>>([]);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState<boolean>(false);
  const [canModifyRules, setCanModifyRules] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showReconfigureMetrics, setShowReconfigureMetrics] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const showReconfigureParimaryMetrics = !adGroupId && !isAdGroupMultiSelect;
  const showPublishBidAndBidModificationHistory = !isAdGroupMultiSelect;
  const showResetCampaignRules =
    !showConfirmationMessage && ruleFetchingState === 'success' && hasRules && !adGroupId && !isAdGroupMultiSelect;
  const [campaignConfig, setCampaignConfig] = useState<publicOfferFeedService.GoogleAdsCampaignConfigItem | null>(null);
  const { control, handleSubmit, reset } = useForm<RuleDefinitionFormData>({
    defaultValues: generateDefaultValues(),
  });

  useEffect(() => {
    if (ruleDefinitions) {
      reset(generateResetValues(ruleDefinitions));
    }
  }, [ruleDefinitions, reset]);

  const fetchRules = useCallback(
    async (campaignId: string, adGroupId: string) => {
      try {
        setRuleFetchingState('loading');
        const { result } = await getRules(campaignId, adGroupId);
        setHasRules(result !== null);
        setRuleClassifications(result?.rule_classifications);
        setPrimaryMetrics(result?.primary_metrics);
        const modifyAllowed = adGroupId ? result?.is_overridden : true;
        setCanModifyRules(modifyAllowed);
        setRuleDefinitions(result?.rule_definitions);
        setRuleFetchingState('success');
      } catch (error) {
        setRuleFetchingState('failed');
        enqueueSnackbar('Failed to fetch data', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  const fetchCampaignConfig = useCallback(async () => {
    const response = await listGoogleAdsCampaignsConfig({
      customerId,
      campaignId,
    });
    setCampaignConfig(response.result?.campaigns[0] || null);
  }, [customerId, campaignId]);

  useEffect(() => {
    fetchCampaignConfig();
  }, [fetchCampaignConfig]);

  useEffect(() => {
    fetchRules(campaignId, adGroupId);
  }, [campaignId, adGroupId, fetchRules]);

  const handleBidClassificationChange = useCallback((key: string, value: string) => {
    setRuleClassifications((prev) => ({
      ...prev,
      [key]: +value,
    }));
  }, []);

  const handleMetricSelection = useCallback(
    (metric: string, checked: boolean) => {
      setSelectedMetrics((prev) => {
        if (checked) {
          if (prev.length < 3) {
            return [...prev, metric];
          } else {
            enqueueSnackbar('You can only select up to 3 metrics', { variant: 'warning' });
            return prev;
          }
        } else {
          return prev.filter((m) => m !== metric);
        }
      });
    },
    [enqueueSnackbar],
  );

  const submitSelectedMetrics = useCallback(async () => {
    if (selectedMetrics.length !== 3) {
      enqueueSnackbar('Please select exactly 3 metrics', { variant: 'warning' });
      return;
    }

    try {
      await publicOfferFeedService.createCampaignRules(campaignId, selectedMetrics, adGroupId);
      enqueueSnackbar('Successfully updated primary metrics', { variant: 'success' });
      await fetchRules(campaignId, adGroupId);
    } catch (error) {
      enqueueSnackbar('Failed to update primary metrics', { variant: 'error' });
    }
  }, [campaignId, adGroupId, selectedMetrics, fetchRules, enqueueSnackbar]);

  const updateSelectedMetrics = useCallback(async () => {
    if (selectedMetrics.length !== 3) {
      enqueueSnackbar('Please select exactly 3 metrics', { variant: 'warning' });
      return;
    }

    try {
      await publicOfferFeedService.updatePrimaryMetrics(campaignId, selectedMetrics, adGroupId);
      enqueueSnackbar('Successfully updated primary metrics', { variant: 'success' });
      setShowReconfigureMetrics(false);
      setSelectedMetrics([]);
      await fetchRules(campaignId, adGroupId);
    } catch (error) {
      enqueueSnackbar('Failed to update primary metrics', { variant: 'error' });
    }
  }, [campaignId, adGroupId, selectedMetrics, fetchRules, enqueueSnackbar]);

  const handleClassificationSubmit = useCallback(async () => {
    try {
      await publicOfferFeedService.updateRuleClassifications({ campaignId, adGroupId, ruleClassifications });
      enqueueSnackbar('Successfully updated rule classifications', { variant: 'success' });
      await fetchRules(campaignId, adGroupId);
    } catch (error) {
      enqueueSnackbar('Failed to update rule classifications', { variant: 'error' });
    }
  }, [campaignId, adGroupId, ruleClassifications, fetchRules, enqueueSnackbar]);

  const ruleDefinitionsOnSubmit = useCallback(
    async (data: RuleDefinitionFormData) => {
      try {
        const primary = Object.fromEntries(primaryMetrics.map((metric) => [metric, data[metric]]));
        await publicOfferFeedService.updateRuleDefinitions({ campaignId, adGroupId, ruleDefinitions: primary });
        enqueueSnackbar('Successfully updated rule definitions', { variant: 'success' });
        await fetchRules(campaignId, adGroupId);
      } catch (error) {
        enqueueSnackbar('Failed to update rule definitions', { variant: 'error' });
      }
    },
    [campaignId, adGroupId, fetchRules, enqueueSnackbar, primaryMetrics],
  );

  const updateMultipleAdGroupRules = (data: RuleDefinitionFormData) => {
    const definitions = Object.fromEntries(primaryMetrics.map((metric) => [metric, data[metric]]));
    if (isAdGroupMultiSelect && selectedAdGroupIds?.length > 0) {
      publicOfferFeedService
        .bulkCreateOrUpdateRules({
          campaignId: Number(campaignId),
          adGroupIds: selectedAdGroupIds.map((id) => Number(id)),
          ruleDefinitions: definitions,
          ruleClassifications,
        })
        .then(() => {
          enqueueSnackbar('Successfully updated rules for selected ad groups', { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar('Failed to update rules for selected ad groups', { variant: 'error' });
        });
    }
  };

  const triggerReset = useCallback(async () => {
    try {
      await deleteAllCustomRules(campaignId);
      setShowConfirmationMessage(true);
      enqueueSnackbar('Successfully deleted all the custom rule definitions', { variant: 'success' });
    } catch (error) {
      setError('Failed to delete custom rule definitions');
      enqueueSnackbar('Failed to delete custom rule definitions', { variant: 'error' });
    }
  }, [campaignId, enqueueSnackbar, setShowConfirmationMessage, setError]);

  const triggerOverride = useCallback(async () => {
    try {
      await publicOfferFeedService.createCampaignRules(campaignId, primaryMetrics, adGroupId);
      enqueueSnackbar('Ad Group rules created successfully!', { variant: 'success' });
      await fetchRules(campaignId, adGroupId);
    } catch (error) {
      console.error('Failed to create Ad Group rules:', error);
      enqueueSnackbar('Failed to create Ad Group rules.', { variant: 'error' });
    }
  }, [campaignId, adGroupId, fetchRules, enqueueSnackbar, primaryMetrics]);

  const deleteCustomRule = useCallback(async () => {
    try {
      await publicOfferFeedService.deleteCustomRule(campaignId, adGroupId);
      enqueueSnackbar('Ad Group rules deleted successfully!', { variant: 'success' });
      await fetchRules(campaignId, adGroupId);
    } catch (error) {
      console.error('Failed to delete rules:', error);
      enqueueSnackbar('Failed to delete rules', { variant: 'error' });
    }
  }, [campaignId, adGroupId, fetchRules, enqueueSnackbar]);

  const publishBid = useCallback(async () => {
    try {
      await publicOfferFeedService.publishAdGroupBid(customerId, campaignId, adGroupId);
      enqueueSnackbar('Successfully published!', { variant: 'success' });
      if (fetchData) {
        fetchData(customerId, campaignId, adGroupId);
      }
    } catch (error) {
      console.error('Failed to publish bids', error);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [customerId, campaignId, adGroupId, fetchData, enqueueSnackbar]);

  return (
    <>
      <PageSubheader title="Primary Metrics" />

      {hasRules && (
        <Box>
          <Typography variant="h6" mb={2}>
            Current Primary Metrics:
          </Typography>
          {primaryMetrics &&
            primaryMetrics.map((metric, index) => (
              <Typography key={metric}>
                • Metric {index + 1} - {RULE_HEADINGS[metric] || metric}
              </Typography>
            ))}
          {showReconfigureParimaryMetrics && (
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => setShowReconfigureMetrics(true)}
              disabled={showReconfigureMetrics}
            >
              Reconfigure Primary Metrics
            </Button>
          )}
        </Box>
      )}

      {(!hasRules || showReconfigureMetrics) && (
        <Paper sx={{ my: 4, p: 3 }} className="branded-panel">
          <Typography variant="h6" mb={2}>
            Select 3 Primary Metrics
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Choose exactly 3 metrics that will be used for bid automation rules.
          </Typography>

          <Stack spacing={2} mb={3}>
            {[
              ...ALL_METRICS_SECTION,
              ...(campaignConfig?.campaign_type === 'search' ? SEARCH_METRICS_SECTION : []),
            ].map((metric) => (
              <FormControlLabel
                key={metric}
                control={
                  <Checkbox
                    checked={selectedMetrics.includes(metric)}
                    onChange={(e) => handleMetricSelection(metric, e.target.checked)}
                    disabled={!selectedMetrics.includes(metric) && selectedMetrics.length >= 3}
                  />
                }
                label={RULE_HEADINGS[metric]}
              />
            ))}
          </Stack>

          {selectedMetrics.length > 0 && (
            <Box mb={3}>
              <Typography variant="subtitle2" mb={1}>
                Selected Metrics ({selectedMetrics.length}/3):
              </Typography>
              {selectedMetrics.map((metric, index) => (
                <Typography key={metric} variant="body2" color="primary">
                  • Metric {index + 1} - {RULE_HEADINGS[metric]}
                </Typography>
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={showReconfigureMetrics ? updateSelectedMetrics : submitSelectedMetrics}
            disabled={selectedMetrics.length !== 3}
          >
            {showReconfigureMetrics ? 'Update Primary Metrics' : 'Create Campaign Rules'} ({selectedMetrics.length}/3)
          </Button>

          {showReconfigureMetrics && (
            <Button
              variant="outlined"
              sx={{ ml: 2 }}
              onClick={() => {
                setShowReconfigureMetrics(false);
                setSelectedMetrics([]);
              }}
            >
              Cancel
            </Button>
          )}
        </Paper>
      )}

      <PageSubheader title="Configure Rules" />

      {ruleFetchingState === 'loading' && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      )}

      {!showConfirmationMessage && ruleFetchingState === 'success' && hasRules && adGroupId && !canModifyRules && (
        <>
          <Typography>
            Creating custom Ad Group rules will override the campaign-level defaults and allow to set specific
            configurations for this Ad Group.
          </Typography>

          <Box mt={1}>
            <ConfirmButton variant="contained" position="top" onConfirm={triggerOverride}>
              Create Custom Rules
            </ConfirmButton>
          </Box>
        </>
      )}

      <form onSubmit={handleSubmit(updateMultipleAdGroupRules)}>
        {ruleDefinitions && (
          <Paper sx={{ my: 4 }} id="definitions" className="branded-panel">
            <Typography component="h1" variant="h3">
              Rule Definitions
            </Typography>
            <Stack spacing={4} mt={2}>
              {primaryMetrics.map((section) => (
                <Box key={section}>
                  <Typography variant="h6" mb={2}>
                    {RULE_HEADINGS[section]}
                  </Typography>
                  <Grid container spacing={2}>
                    {RULE_LEVELS.map((level) => (
                      <Grid item xs={4} key={`${section}-${level}`}>
                        <Controller
                          name={`${section}.${level}` as keyof RuleDefinitionFormData}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              type="number"
                              label={LEVEL_LABELS[level]}
                              inputProps={{ step: '0.01' }}
                              disabled={!canModifyRules}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                if (isNaN(value)) {
                                  field.onChange(null);
                                } else {
                                  field.onChange(parseFloat(value.toFixed(2)));
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Stack>

            {!isAdGroupMultiSelect && (
              <Stack spacing={2} direction="row" mt={4}>
                <Button onClick={handleSubmit(ruleDefinitionsOnSubmit)} variant="contained" disabled={!canModifyRules}>
                  Update
                </Button>
              </Stack>
            )}
          </Paper>
        )}

        {ruleClassifications && (
          <Paper sx={{ my: 4 }} id="classification" className="branded-panel">
            <Typography component="h1" variant="h3">
              Rule Classifications
            </Typography>
            <Typography>
              These values represent bid adjustment targets that will be automatically applied in Google Ads.
            </Typography>
            <Grid container spacing={3} mb={4} mt={4}>
              {Object.keys(ruleClassifications ?? {}).map((item) => (
                <Grid item sm={3} key={item}>
                  <TextField
                    autoFocus
                    type="number"
                    value={ruleClassifications[item] || ''}
                    onChange={(e) => handleBidClassificationChange(item, e.target.value)}
                    label={item}
                    placeholder={item}
                    disabled={!canModifyRules}
                  />
                </Grid>
              ))}
            </Grid>
            {!isAdGroupMultiSelect && (
              <Stack spacing={2} direction="row">
                <Button variant="contained" onClick={handleClassificationSubmit} disabled={!canModifyRules}>
                  Update
                </Button>
              </Stack>
            )}
          </Paper>
        )}

        {isAdGroupMultiSelect && (
          <Button type="submit" variant="contained" sx={{ mt: 2, mb: 2 }}>
            Update Rules
          </Button>
        )}
      </form>

      {ruleFetchingState === 'success' && !hasRules && !adGroupId && (
        <Typography>
          This campaign doesn't have any rules configured. Click below to set up default campaign rules.
        </Typography>
      )}

      {error && <ErrorDisplay message={error} />}

      {showConfirmationMessage && (
        <Alert severity="success">
          <strong>Success!</strong> Success
        </Alert>
      )}

      {showResetCampaignRules && (
        <>
          <Typography>
            Warning: Resetting campaign rules will remove all custom Ad Group rule configurations for this campaign and
            restore default settings.
          </Typography>

          <Box mt={1}>
            <ConfirmButton variant="contained" position="top" onConfirm={triggerReset}>
              Reset Campaign Rules
            </ConfirmButton>
          </Box>
        </>
      )}

      {!showConfirmationMessage && ruleFetchingState === 'success' && hasRules && adGroupId && canModifyRules && (
        <>
          <Typography>
            Warning: Deleting Ad Group level rules will revert this Ad Group back to using campaign-level rule
            configurations.
          </Typography>

          <Box mt={1}>
            <ConfirmButton variant="contained" color="error" position="top" onConfirm={deleteCustomRule}>
              Delete Custom Rules
            </ConfirmButton>
          </Box>
        </>
      )}

      {showPublishBidAndBidModificationHistory && (
        <>
          <Paper sx={{ my: 4 }} id="publish" className="branded-panel">
            <PageSubheader title="Publish Bid" />
            <Typography>
              {adGroupId
                ? `Warning: Publishing will change the bid target value in Google Ads.`
                : `Functionality not available in Campaign level. Please use Ad Group level.`}
            </Typography>

            <Box mt={1}>
              <ConfirmButton
                variant="contained"
                color="primary"
                position="top"
                onConfirm={publishBid}
                disabled={!adGroupId}
              >
                Publish
              </ConfirmButton>
            </Box>
          </Paper>
          <Paper sx={{ my: 4 }} id="history" className="branded-panel">
            <PageSubheader title="Bid Modification History" />

            <Box mt={1}>
              <Button
                component={RouterLink}
                to={`/marketing/google-ads/bid-modification-history?customerId=${customerId}&campaignId=${campaignId}&adGroupId=${
                  adGroupId ?? ''
                }`}
                variant="outlined"
                sx={{ mt: 2 }}
              >
                View History
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </>
  );
}
